import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { formatCurrency } from '../utils/helpers';
import { logoStorageService } from '../services/storageService';

const InvoiceModal = ({ invoice, onClose }) => {
  const { garageInfo } = useApp();
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [logoUrl, setLogoUrl] = useState('');

  useEffect(() => {
    let mounted = true;
    const loadLogo = async () => {
      try {
        const url = await logoStorageService.getLogoURL();
        if (mounted && url) setLogoUrl(url);
      } catch (e) {
        console.error('InvoiceModal: Error loading logo', e);
      }
    };
    loadLogo();
    const onBrandingUpdate = (e) => {
      if (e.detail?.logo !== undefined && mounted) setLogoUrl(e.detail.logo || '');
    };
    window.addEventListener('brandingUpdated', onBrandingUpdate);
    return () => {
      mounted = false;
      window.removeEventListener('brandingUpdated', onBrandingUpdate);
    };
  }, []);

  if (!invoice) return null;

  const invoiceDate = new Date(invoice.date);
  const formattedDate = invoiceDate.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });

  const handlePrint = () => {
    // Scroll modal to top so the logo is visible in the print
    const modal = document.querySelector('.invoice-modal');
    if (modal) modal.scrollTop = 0;
    document.body.classList.add('print-invoice');
    const cleanup = () => {
      document.body.classList.remove('print-invoice');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  const buildEmailContent = () => {
    // Build email subject
    const subject = encodeURIComponent(`Invoice ${invoice.number} - ${invoice.reg || 'Vehicle Invoice'}`);

    // Build email body with formatted invoice details
    const emailBody = [
      `Dear Customer,`,
      ``,
      `Please find attached invoice details for your vehicle:`,
      ``,
      `INVOICE DETAILS`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `Invoice Number: ${invoice.number}`,
      `Invoice Date: ${formattedDate}`,
      ``,
      `COMPANY INFORMATION`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      ...(logoUrl ? [`Company logo: ${logoUrl}`, ``] : []),
      `${garageInfo?.companyName || 'Richlee Motor Company'}`,
      `${garageInfo?.address || '123 Motor Street, London, SW1A 1AA'}`,
      `${garageInfo?.contact || 'Phone: 020 1234 5678'}`,
      garageInfo?.email ? garageInfo.email : '',
      garageInfo?.vatNumber ? `VAT: ${garageInfo.vatNumber}` : 'VAT: GB123456789',
      ``,
      `VEHICLE INFORMATION`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `Registration: ${invoice.reg || 'N/A'}`,
      `Vehicle: ${invoice.vehicle || 'N/A'}`,
      `MOT Status: ${invoice.motStatus || 'N/A'}`,
      `Drop-off Date: ${invoice.dropOffDate || 'Not set'}`,
      `Collection Date: ${invoice.collectionDate || 'Not set'}`,
      ...(invoice.estimatedCost !== undefined && invoice.estimatedCost > 0 ? [
        `Estimated Cost: ${formatCurrency(invoice.estimatedCost)}`
      ] : []),
      ``,
      `JOB SUMMARY`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `${invoice.summary || 'No summary provided'}`,
      ``,
      ...(invoice.lineItems?.length > 0 || (invoice.labourCost !== undefined && invoice.labourCost > 0) ? [
        `ITEMISED BILL`,
        `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
        ...(invoice.labourCost > 0 ? [`Labour: ${formatCurrency(invoice.labourCost)}`] : []),
        ...(invoice.lineItems || []).map(item => `${item.description || 'Item'}: ${formatCurrency(parseFloat(item.cost) || 0)}`),
        ``,
      ] : []),
      `PAYMENT DETAILS`,
      `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`,
      `Subtotal: ${formatCurrency(invoice.subtotal || 0)}`,
      `VAT (20%): ${formatCurrency(invoice.vat || 0)}`,
      `Total Amount: ${formatCurrency(invoice.total || 0)}`,
      ``,
      `Thank you for your business.`,
      ``,
      `Best regards,`,
      `${garageInfo?.companyName || 'Richlee Motor Company'}`,
    ].filter(line => line !== '').join('\n');

    return {
      subject,
      body: encodeURIComponent(emailBody)
    };
  };

  const handleEmail = () => {
    // Extract email from contact field (if it contains an email)
    const contactField = invoice.contact || '';
    const emailMatch = contactField.match(/[\w.-]+@[\w.-]+\.\w+/);
    const customerEmail = emailMatch ? emailMatch[0] : '';

    const { subject, body } = buildEmailContent();

    if (customerEmail) {
      // Email found - open email client directly
      const mailtoLink = `mailto:${customerEmail}?subject=${subject}&body=${body}`;
      window.location.href = mailtoLink;
      
      setTimeout(() => {
        alert(`Email client opened with invoice details.\n\nIf your email client didn't open, please copy the invoice details manually.`);
      }, 500);
    } else {
      // No email found - show input modal
      setShowEmailInput(true);
      setEmailInput('');
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    
    if (!emailInput || !emailInput.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    const { subject, body } = buildEmailContent();
    const mailtoLink = `mailto:${emailInput}?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
    
    setShowEmailInput(false);
    setEmailInput('');
    
    setTimeout(() => {
      alert('Email client opened with invoice details.');
    }, 500);
  };

  const handleEmailCancel = () => {
    setShowEmailInput(false);
    setEmailInput('');
  };

  return (
    <div 
      className="modal-backdrop" 
      style={{ display: 'flex' }}
      onClick={(e) => {
        if (e.target.classList.contains('modal-backdrop')) {
          onClose();
        }
      }}
    >
      <div className="modal invoice-modal">
        <button 
          className="modal-close" 
          type="button" 
          onClick={onClose}
          aria-label="Close invoice"
        >
          ×
        </button>
        <div className="invoice-date">
          <div className="invoice-label">Invoice Date</div>
          <div className="invoice-value">{formattedDate}</div>
          <div className="invoice-label" style={{ marginTop: '12px' }}>Invoice Number</div>
          <div className="invoice-value">{invoice.number}</div>
        </div>
        <div className="invoice-container" id="invoiceContainer">
          <div className="invoice-header">
            <div className="invoice-brand">
              {logoUrl && (
                <div className="invoice-logo-wrap">
                  <img src={logoUrl} alt="Company Logo" className="invoice-logo" />
                </div>
              )}
              <h1>{garageInfo?.companyName || 'Richlee Motor Company'}</h1>
              <div className="invoice-garage-info">
                <div className="invoice-value">{garageInfo?.address || '123 Motor Street, London, SW1A 1AA'}</div>
                <div className="invoice-value">{garageInfo?.contact || 'Phone: 020 1234 5678'}</div>
                {garageInfo?.email && (
                  <div className="invoice-value">{garageInfo.email}</div>
                )}
                <div className="invoice-value">
                  {garageInfo?.vatNumber ? `VAT: ${garageInfo.vatNumber}` : 'VAT: GB123456789'}
                </div>
              </div>
            </div>
          </div>

          <div className="invoice-details">
            <div className="invoice-section">
              <div className="invoice-label">Vehicle Registration</div>
              <div className="invoice-value">{invoice.reg || 'N/A'}</div>
            </div>
            <div className="invoice-section">
              <div className="invoice-label">Vehicle</div>
              <div className="invoice-value">{invoice.vehicle || 'N/A'}</div>
            </div>
            <div className="invoice-section">
              <div className="invoice-label">Customer Contact</div>
              <div className="invoice-value">{invoice.contact || 'N/A'}</div>
            </div>
          </div>

          <div className="invoice-summary-section">
            <div className="invoice-label">Job Summary</div>
            <div className="invoice-value">{invoice.summary || 'No summary provided'}</div>
          </div>

          <div className="invoice-details">
            <div className="invoice-section">
              <div className="invoice-label">MOT Status</div>
              <div className="invoice-value">{invoice.motStatus || 'N/A'}</div>
            </div>
            <div className="invoice-section">
              <div className="invoice-label">Drop-off Date</div>
              <div className="invoice-value">{invoice.dropOffDate || 'Not set'}</div>
            </div>
            <div className="invoice-section">
              <div className="invoice-label">Collection Date</div>
              <div className="invoice-value">{invoice.collectionDate || 'Not set'}</div>
            </div>
            {invoice.estimatedCost !== undefined && invoice.estimatedCost > 0 && (
              <div className="invoice-section">
                <div className="invoice-label">Estimated Cost</div>
                <div className="invoice-value">{formatCurrency(invoice.estimatedCost)}</div>
              </div>
            )}
          </div>

          {(invoice.lineItems?.length > 0 || (invoice.labourCost !== undefined && invoice.labourCost > 0)) && (
            <div className="invoice-summary-section" style={{ marginBottom: '16px' }}>
              <div className="invoice-label" style={{ marginBottom: '12px', fontWeight: 600 }}>Itemised Bill</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {invoice.labourCost > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                    <span>Labour</span>
                    <span>{formatCurrency(invoice.labourCost)}</span>
                  </div>
                )}
                {(invoice.lineItems || []).map((item, index) => (
                  <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border, #e5e7eb)' }}>
                    <span>{(item.description || 'Item').charAt(0).toUpperCase() + (item.description || 'Item').slice(1)}</span>
                    <span>{formatCurrency(parseFloat(item.cost) || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="invoice-total-section">
            <div className="invoice-subtotal">
              <div className="invoice-total-label">Subtotal</div>
              <div className="invoice-total-value">{formatCurrency(invoice.subtotal || 0)}</div>
            </div>
            <div className="invoice-vat-row">
              <div className="invoice-total-label">VAT (20%)</div>
              <div className="invoice-total-value">{formatCurrency(invoice.vat || 0)}</div>
            </div>
            <div className="invoice-total">
              <div className="invoice-total-label">Total Amount</div>
              <div className="invoice-total-value">{formatCurrency(invoice.total || 0)}</div>
            </div>
          </div>

          <div className="invoice-actions">
            <button 
              type="button" 
              className="primary-btn" 
              id="printInvoiceBtn"
              onClick={handlePrint}
            >
              Print Invoice
            </button>
            <button 
              type="button" 
              className="primary-btn" 
              id="emailInvoiceBtn"
              onClick={handleEmail}
            >
              Email Invoice
            </button>
          </div>
        </div>
      </div>

      {/* Email Input Modal */}
      {showEmailInput && (
        <div 
          className="modal-backdrop" 
          style={{ 
            display: 'flex',
            zIndex: 10001,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => {
            if (e.target.classList.contains('modal-backdrop')) {
              handleEmailCancel();
            }
          }}
        >
          <div 
            className="modal" 
            style={{
              maxWidth: '500px',
              width: '90%',
              padding: '24px',
              backgroundColor: 'var(--card-bg)',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-dark)' }}>
                Enter Customer Email
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
                No email address found in customer contact. Please enter the customer's email address:
              </p>
            </div>
            <form onSubmit={handleEmailSubmit}>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="customer@example.com"
                required
                autoFocus
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '16px',
                  borderRadius: '8px',
                  border: '1px solid var(--border)',
                  fontSize: '14px',
                  backgroundColor: 'var(--card-bg)',
                  color: 'var(--text-dark)',
                  boxSizing: 'border-box'
                }}
              />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleEmailCancel}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    backgroundColor: 'transparent',
                    color: 'var(--text-dark)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#4d8063',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Send Email
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceModal;

